import {
  ChaveDuplicada,
  EntregadorCadastrado,
  LinhaEntregador,
  NotaDoDia,
  ResumoConferencia,
} from './types';

/**
 * Regra da conferencia do dia.
 *
 * Responde as tres perguntas que o operador faz antes de a rota sair: quem
 * esta com quantas notas, quem ficou sem carga e se ha nota repetida.
 */

const SEM_MANIFESTO = '—';

const semProdutos = (nota: NotaDoDia) =>
  !nota.produtos || nota.produtos.length === 0;

/**
 * Uma linha por entregador.
 *
 * A lista de entregadores CADASTRADOS entra junto de proposito: motorista sem
 * manifesto nao aparece nas notas, e um relatorio montado so a partir delas
 * esconderia justamente o caso que precisa de acao.
 */
export const agrupaPorEntregador = (
  notas: NotaDoDia[],
  entregadores: EntregadorCadastrado[]
): LinhaEntregador[] => {
  // internamente manifestos e LISTA; vira texto so na saida. Interseccao
  // com LinhaEntregador daria `string & string[]`, que nao existe.
  type Acumulador = Omit<LinhaEntregador, 'manifestos'> & { manifestos: string[] };
  const porCodigo = new Map<string, Acumulador>();

  const garante = (codigo: string, nome: string) => {
    if (!porCodigo.has(codigo)) {
      porCodigo.set(codigo, {
        codigousuario: codigo,
        entregador: nome,
        notas: 0,
        manifestos: [],
        semProdutos: 0,
        itens: 0,
      });
    }
    return porCodigo.get(codigo)!;
  };

  notas.forEach((nota) => {
    const linha = garante(String(nota.codigousuario), nota.usuario);
    linha.notas += 1;
    linha.itens += nota.produtos?.length ?? 0;
    if (semProdutos(nota)) linha.semProdutos += 1;

    const manifesto = nota.numeromanifesto
      ? String(nota.numeromanifesto)
      : null;
    if (manifesto && !linha.manifestos.includes(manifesto)) {
      linha.manifestos.push(manifesto);
    }
  });

  entregadores.forEach((entregador) =>
    garante(String(entregador.codigousuario), entregador.nomeusuario)
  );

  return Array.from(porCodigo.values())
    .map((linha) => ({
      ...linha,
      manifestos: linha.manifestos.length
        ? [...linha.manifestos].sort().join(', ')
        : SEM_MANIFESTO,
    }))
    .sort(
      (a, b) =>
        b.notas - a.notas || a.entregador.localeCompare(b.entregador, 'pt-BR')
    );
};

/** Chaves lancadas mais de uma vez no dia, para o mesmo ou outro entregador. */
export const chavesDuplicadas = (notas: NotaDoDia[]): ChaveDuplicada[] => {
  const porChave = new Map<string, NotaDoDia[]>();

  notas.forEach((nota) => {
    const lista = porChave.get(nota.chave) ?? [];
    lista.push(nota);
    porChave.set(nota.chave, lista);
  });

  return Array.from(porChave.entries())
    .filter(([, lista]) => lista.length > 1)
    .map(([chave, lista]) => ({
      chave,
      quantidade: lista.length,
      entregadores: Array.from(new Set(lista.map((n) => n.usuario))).sort(
        (a, b) => a.localeCompare(b, 'pt-BR')
      ),
    }))
    .sort((a, b) => b.quantidade - a.quantidade || a.chave.localeCompare(b.chave));
};

export const resumoConferencia = (
  notas: NotaDoDia[],
  entregadores: EntregadorCadastrado[]
): ResumoConferencia => {
  const linhas = agrupaPorEntregador(notas, entregadores);

  return {
    notas: notas.length,
    comCarga: linhas.filter((l) => l.notas > 0).length,
    semCarga: linhas.filter((l) => l.notas === 0).length,
    semProdutos: notas.filter(semProdutos).length,
    duplicadas: chavesDuplicadas(notas).length,
  };
};
